import * as React from 'react';
import {
    Datagrid,
    Edit,
    NumberInput,
    BooleanInput,
    ReferenceManyField,
    TextInput,
    TabbedForm,
    FormTab,
    SaveButton,
    Toolbar,
    FileInput,
    FileField,
    ReferenceField,
    FunctionField,
    DateField,
    DateInput,
    Labeled,
    useTranslate,
    TextField,
    BooleanField,
    useEditContext,
    useGetIdentity,
    useRecordContext,
    useResourceContext,
} from 'react-admin';
import {
    required,
    minValue,
    maxValue,
    minLength,
    maxLength,
    number,
    email,
    regex,
    choices,
} from 'react-admin';
import { Grid, Box, Typography, Stack } from '@mui/material';

import {
    CustomSelectInput,
    CustomSelectArrayInput,
    CustomCheckboxGroupInput,
    CustomRadioButtonGroupInput,
    CustomJSONPretty,
    CustomTextField,
    CustomBulkDeleteButton,
    CustomBulkAddButton,
    CustomProductList,
    CustomNumberInput,
    CustomLogEventList,
    CustomUserLicenseByUserList,
    CustomUserSchoolList,
} from '../controls';

import {
    LibraryBooks,
    Article,
    Podcasts,
    Attachment,
    DataObject,
    TransferWithinAStation,
    History,
    Computer,
} from '@mui/icons-material';

import { useCanAccess } from '@react-admin/ra-rbac';
import { EditableDatagrid, RowForm } from '@react-admin/ra-editable-datagrid';
import CustomStatistics from '../controls/CustomStatistics';
import StatsWithIcon from '../../components/statistics/controls/StatsWithIcon';

const ADMIN_APP_GUID = '13b312b5-367c-4c74-bb77-bdb34df4e721';

const useCanEditUser = () => {
    const record = useRecordContext();
    const { identity } = useGetIdentity();
    const currentRights = identity?.userrights || 0;
    const targetApp = record?.application?.find(a => a.id === ADMIN_APP_GUID);
    const targetRights = targetApp?.userrights || 0;
    return currentRights >= targetRights;
};

const EditToolbar = () => {
    const canEditByRights = useCanEditUser();
    const resource = useResourceContext();
    const { canAccess: canEditByRbac } = useCanAccess({
        action: 'write',
        resource,
    });
    const canEdit = canEditByRights && canEditByRbac;
    return (
        <Toolbar>
            {canEdit ? (
                <SaveButton />
            ) : (
                <Typography variant="body2" color="warning.main">
                    Käyttäjällä on korkeammat oikeudet kuin sinulla. Muokkaus ei
                    ole sallittu.
                </Typography>
            )}
        </Toolbar>
    );
};

const ApplicationForm = () => {
    const useEdit = useEditContext();
    //console.log('record', record);
    return (
        <RowForm defaultValues={{ user_guid: useEdit.record.guid }}>
            <CustomSelectInput
                resource="userapplication"
                source="application_guid"
                url="extralist/userapplication/applications"
                label=""
                validate={[required()]}
            />
            <BooleanInput
                resource="userapplication"
                source="isadmin"
                label=""
            />
            <CustomSelectInput
                resource="userapplication"
                source="public.userrights"
                url="extralist/userapplication/userrights"
                label=""
                showIfControl="application_guid"
                showIfValue="13b312b5-367c-4c74-bb77-bdb34df4e721"
                validate={[required()]}
            />
            <CustomSelectArrayInput
                resource="userapplication"
                source="public.envs"
                url="extralist/userapplication/envs"
                label=""
                showIfControl="application_guid"
                showIfValue="13b312b5-367c-4c74-bb77-bdb34df4e721"
            />
            <CustomSelectInput
                resource="userapplication"
                source="status"
                url="extralist/userapplication/statuses"
                label=""
                validate={[required()]}
            />
        </RowForm>
    );
};

const ApplicationDatagrid = ({ children }: { children?: React.ReactNode }) => {
    const canEdit = useCanEditUser();
    if (!canEdit) {
        return <Datagrid bulkActionButtons={false}>{children}</Datagrid>;
    }
    return (
        <EditableDatagrid
            bulkActionButtons={false}
            createForm={<ApplicationForm />}
            editForm={<ApplicationForm />}
        >
            {children}
        </EditableDatagrid>
    );
};

const UserEdit = () => {
    const translate = useTranslate();

    return (
        <Edit
            redirect={false}
            mutationMode="optimistic"
            queryOptions={{ refetchOnMount: 'always' }}
        >
            <TabbedForm toolbar={<EditToolbar />}>
                <FormTab label="resources.user.tabs.profile" icon={<Article />}>
                    <Grid container width={{ xs: '100%' }} spacing={2}>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <TextInput
                                resource="user"
                                source="public.firstname"
                                validate={[required()]}
                                fullWidth
                            />
                            <TextInput
                                resource="user"
                                source="public.lastname"
                                validate={[required()]}
                                fullWidth
                            />
                            <TextInput
                                resource="user"
                                source="username"
                                validate={[required()]}
                                fullWidth
                            />
                            <TextInput
                                resource="user"
                                source="public.description"
                                fullWidth
                            />
                            <CustomSelectInput
                                resource="user"
                                source="status"
                                url="extralist/user/statuses"
                                parse={v => parseInt(v)}
                                fullWidth
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <Typography variant="subtitle1">
                                {translate('resources.user.fields.group.valid')}
                            </Typography>
                            <Typography variant="body2">
                                {translate(
                                    'resources.user.fields.group.valid_desc'
                                )}
                            </Typography>
                            <Box display={{ xs: 'block', sm: 'flex' }}>
                                <DateInput
                                    resource="user"
                                    source="public.validfrom"
                                />
                            </Box>
                            <Box display={{ xs: 'block', sm: 'flex' }}>
                                <DateInput
                                    resource="user"
                                    source="public.validto"
                                />
                            </Box>

                            <BooleanInput
                                resource="user"
                                source="public.login_not_allowed"
                            />
                            <BooleanInput
                                resource="user"
                                source="public.profile_locked"
                            />

                            <Box
                                display={{ xs: 'block', sm: 'flex' }}
                                sx={{ marginBottom: '10px' }}
                            >
                                <Labeled>
                                    <DateField
                                        resource="user"
                                        source="last_login"
                                        showTime
                                    />
                                </Labeled>
                            </Box>
                            <Stack direction="row" spacing={4}>
                                <Box>
                                    <Labeled label="resources.teacher.fields.author.creator">
                                        <>
                                            <ReferenceField
                                                reference="user"
                                                source="author.creator"
                                            >
                                                <FunctionField
                                                    render={record =>
                                                        `${record.public.firstname} ${record.public.lastname}`
                                                    }
                                                />
                                            </ReferenceField>
                                            <DateField
                                                resource="user"
                                                source="registered"
                                                showTime
                                                locales={'fi-FI'}
                                            />
                                        </>
                                    </Labeled>
                                </Box>
                                <Box>
                                    <Labeled label="resources.teacher.fields.author.modifier">
                                        <>
                                            <ReferenceField
                                                reference="user"
                                                source="author.modifier"
                                            >
                                                <FunctionField
                                                    render={record =>
                                                        `${record.public.firstname} ${record.public.lastname}`
                                                    }
                                                />
                                            </ReferenceField>
                                            <DateField
                                                resource="user"
                                                source="author.modified"
                                                showTime
                                                locales={'fi-FI'}
                                            />
                                        </>
                                    </Labeled>
                                </Box>
                            </Stack>
                        </Grid>
                    </Grid>
                </FormTab>
                <FormTab
                    label="resources.user.tabs.application"
                    icon={<Computer />}
                    path="palvelut"
                >
                    <Grid container width={{ xs: '100%' }} spacing={2}>
                        <Grid size={12}>
                            <ReferenceManyField
                                reference="userapplication"
                                target="user_guid"
                            >
                                <ApplicationDatagrid>
                                    <CustomSelectInput
                                        resource="userapplication"
                                        source="application_guid"
                                        label={translate(
                                            'resources.user.fields.application.name'
                                        )}
                                        url="extralist/userapplication/applications"
                                        showAsText={true}
                                    />
                                    <BooleanField
                                        resource="userapplication"
                                        source="isadmin"
                                        label={translate(
                                            'resources.user.fields.application.isadmin'
                                        )}
                                    />
                                    <CustomSelectInput
                                        resource="userapplication"
                                        source="public.userrights"
                                        label={translate(
                                            'resources.user.fields.application.role'
                                        )}
                                        url="extralist/userapplication/userrights"
                                        showAsText={true}
                                    />
                                    <CustomSelectArrayInput
                                        resource="userapplication"
                                        source="public.envs"
                                        label={translate(
                                            'resources.user.fields.application.envs'
                                        )}
                                        url="extralist/userapplication/envs"
                                        showAsText={true}
                                    />
                                    <CustomSelectInput
                                        resource="userapplication"
                                        source="status"
                                        label={translate(
                                            'resources.user.fields.application.status'
                                        )}
                                        url="extralist/userapplication/statuses"
                                        showAsText={true}
                                    />
                                </ApplicationDatagrid>
                            </ReferenceManyField>
                        </Grid>
                    </Grid>
                </FormTab>

                {/* <FormTab label="resources.user.tabs.schools_and_groups" icon={<LibraryBooks />} path="koulut_ja_ryhmat">
                    
                    <Grid container width={{ xs: '100%' }} spacing={2}>
                        <Grid size={12}>
                            <CustomUserSchoolList disableSyncWithLocation storeKey='userSchoolList' resource='usergroup' filter={{type:2}} customFilters={['user_guid']} showExtra={['']} hideFields={['name']} bulkActionButtons={false}><span /></CustomUserSchoolList>
                        </Grid>
                    </Grid>
                    
                </FormTab>
                <FormTab label="resources.teacher.tabs.materials" icon={<LibraryBooks />} path="kaytossa">
                    
                        <Grid container width={{ xs: '100%' }} spacing={2}>
                            <Grid size={12}>
                                <CustomUserLicenseByUserList resource='userlicense' disableSyncWithLocation customFilters={['user_guid']} showExtra={['seasonFilter']} hideFields={[]} sort={{ field: 'product.name', order: 'ASC' }} bulkActionButtons={<CustomBulkDeleteButton />}><span /></CustomUserLicenseByUserList>
                            </Grid>
                        </Grid>
                    
                </FormTab>
                <FormTab label="resources.teacher.tabs.merge" icon={<LibraryBooks />} path="yhdistaminen">
                    
                        <Grid container width={{ xs: '100%' }} spacing={2}>
                            <Grid size={12}>
                                
                            </Grid>
                        </Grid>
                    
                </FormTab> */}
                <FormTab
                    label="resources.teacher.tabs.logs"
                    icon={<History />}
                    path="logit"
                >
                    <Grid container width={{ xs: '100%' }} spacing={2}>
                        <Grid size={12}>
                            <CustomStatistics
                                statisticKey="userEvents"
                                handler={StatsWithIcon}
                                mode="List"
                                from="30d"
                                limit={30}
                                exclude_search={[
                                    'updateAspaProfile',
                                    'FORCE_LOGOUT',
                                ]}
                                include_search={[]}
                                pagination={true}
                                showFilter={true}
                                include_source_fields={[
                                    'infra2.request_detail',
                                ]}
                            />
                        </Grid>
                    </Grid>
                </FormTab>
            </TabbedForm>
        </Edit>
    );
};

const redirect: any = () => {
    return window.location.hash.replace('#', '');
};

export default UserEdit;
